import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../redux/userSlice';
import { toast } from 'react-toastify';
import { hideAuth } from '../../redux/authSlice';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import proxy from '../../api/axios';
import { GOOGLE_SIGN_IN_CLIENT_ID, GITHUB_CLIENT_ID } from '../../common/constants';
import LegalModal from '../LegalModal';
import BrandMark from '../BrandMark';

const ACCENT = '#137274';
const PINK = '#f50057';

const fieldSx = (isDark, border) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0f2a2b', 0.02),
    '& fieldset': { borderColor: border },
    '&:hover fieldset': { borderColor: alpha(ACCENT, 0.55) },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
});

const OTP_LENGTH = 6;

/** Six-box verification code input: one digit per box, auto-advancing focus. */
const OtpInput = ({ value, onChange, isDark, border, autoFocus }) => {
  const inputsRef = useRef([]);
  const digits = value.split('').concat(Array(OTP_LENGTH).fill('')).slice(0, OTP_LENGTH);

  const focusBox = (index) => {
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');

    if (raw.length > 1) {
      // A full/partial code was typed or pasted into one box.
      const chars = raw.split('').slice(0, OTP_LENGTH - index);
      const next = digits.slice();
      chars.forEach((char, offset) => {
        next[index + offset] = char;
      });
      onChange(next.join('').replace(/\s+$/, ''));
      focusBox(Math.min(index + chars.length, OTP_LENGTH - 1));
      return;
    }

    const next = digits.slice();
    next[index] = raw;
    onChange(next.join('').replace(/\s+$/, ''));
    if (raw && index < OTP_LENGTH - 1) {
      focusBox(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusBox(index - 1);
    }
  };

  const handlePaste = (index) => (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    const chars = pasted.split('').slice(0, OTP_LENGTH - index);
    const next = digits.slice();
    chars.forEach((char, offset) => {
      next[index + offset] = char;
    });
    onChange(next.join('').replace(/\s+$/, ''));
    focusBox(Math.min(index + chars.length, OTP_LENGTH - 1));
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1.5 }}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => {
            inputsRef.current[index] = el;
          }}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste(index)}
          onFocus={(e) => e.target.select()}
          autoFocus={autoFocus && index === 0}
          inputProps={{
            inputMode: 'numeric',
            maxLength: 1,
            'aria-label': `Digit ${index + 1} of verification code`,
            style: {
              textAlign: 'center',
              fontSize: '1.3rem',
              fontWeight: 800,
              padding: '12px 0',
              width: '3rem',
            },
          }}
          sx={fieldSx(isDark, border)}
        />
      ))}
    </Box>
  );
};

const AuthModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const openingRequest = useSelector((state) => state.auth.isOpen);

  const [mode, setMode] = useState('login'); // login | signup | signup-otp | signup-username | forgot
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [signupToken, setSignupToken] = useState('');
  const [username, setUsername] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null); // 'terms' | 'privacy' | null

  const isOpen = open || openingRequest;
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.14)';
  const surface = isDark ? '#121a1c' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const switchMode = (next) => {
    setMode(next);
    resetMessages();
    setShowPassword(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const processSuccessSignIn = (data, { isNew = false } = {}) => {
    if (data?.success && data.token && data.user) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      dispatch(login(data.user));
      dispatch(hideAuth());
      onClose();
      toast.success(
        isNew
          ? `Welcome to CrafterHack, ${data.user.name}`
          : `Welcome back, ${data.user.name}`,
      );
      setTimeout(() => {
        window.location.reload();
      }, 400);
      return;
    }
    setError(data?.message || 'Something went wrong. Please try again.');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      resetMessages();
      const { data } = await proxy.post('/auth/google-signin', {
        idToken: credentialResponse.credential,
      });
      processSuccessSignIn(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleFailure = () => {
    setError('Google sign-in was cancelled or failed.');
  };

  const handleGithubSignIn = () => {
    resetMessages();
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem('github_oauth_state', state);

    const redirectUri = `${window.location.origin}/auth/github/callback`;
    const authUrl =
      'https://github.com/login/oauth/authorize' +
      `?client_id=${encodeURIComponent(GITHUB_CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent('read:user user:email')}` +
      `&state=${encodeURIComponent(state)}`;

    const popup = window.open(authUrl, 'github-oauth', 'width=520,height=640');
    if (!popup) {
      setError('Popup blocked. Please allow popups for this site and try again.');
    }
  };

  useEffect(() => {
    const handleGithubMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'github-oauth') return;

      const expectedState = sessionStorage.getItem('github_oauth_state');
      sessionStorage.removeItem('github_oauth_state');

      if (data.error) {
        setError('GitHub sign-in was cancelled or failed.');
        return;
      }
      if (!data.code || !data.state || data.state !== expectedState) {
        setError('GitHub sign-in failed. Please try again.');
        return;
      }

      try {
        setLoading(true);
        const { data: resData } = await proxy.post('/auth/github-signin', {
          code: data.code,
        });
        processSuccessSignIn(resData);
      } catch (err) {
        setError(err?.response?.data?.message || 'GitHub sign-in failed. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener('message', handleGithubMessage);
    return () => window.removeEventListener('message', handleGithubMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    if (!formData.email || !formData.password) {
      setError('Enter your email and password to continue.');
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const { data } = await proxy.post('/auth/login', {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });
      processSuccessSignIn(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e?.preventDefault?.();
    if (!formData.name?.trim() || !formData.email || !formData.password) {
      setError('Fill in name, email, and password.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Use at least 8 characters for your password.');
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const { data } = await proxy.post('/auth/signup', {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });
      if (data?.success) {
        setOtp('');
        setResendCooldown(60);
        setMode('signup-otp');
      } else {
        setError(data?.message || 'Could not create your account.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create your account.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!otp.trim()) {
      setError('Enter the 6-digit code sent to your email.');
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const { data } = await proxy.post('/auth/signup/verify-otp', {
        email: formData.email.trim().toLowerCase(),
        otp: otp.trim(),
      });
      if (data?.success) {
        setSignupToken(data.signupToken);
        setUsername(data.suggestedUsername || '');
        setMode('signup-username');
      } else {
        setError(data?.message || 'Invalid code.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    resetMessages();
    try {
      const { data } = await proxy.post('/auth/signup/resend-otp', {
        email: formData.email.trim().toLowerCase(),
      });
      if (data?.success) {
        setSuccess('A new code has been sent to your email.');
        setResendCooldown(60);
      } else {
        setError(data?.message || 'Could not resend code.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not resend code.');
    }
  };

  const handleCompleteSignup = async (e) => {
    e?.preventDefault?.();
    if (!username.trim()) {
      setError('Choose a username to continue.');
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const { data } = await proxy.post('/auth/signup/complete', {
        signupToken,
        username: username.trim(),
      });
      processSuccessSignIn(data, { isNew: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create your account.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e?.preventDefault?.();
    if (!forgotEmail.trim()) {
      setError('Enter the email associated with your account.');
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const { data } = await proxy.post('/auth/forgot-password', {
        email: forgotEmail.trim(),
      });
      if (data.success) {
        setSuccess(
          data.message ||
            'If an account exists for that email, a reset link has been sent.',
        );
      } else {
        setError(data.message || 'Could not send reset email.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send reset email. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({ email: '', password: '', name: '' });
    setForgotEmail('');
    setOtp('');
    setSignupToken('');
    setUsername('');
    setResendCooldown(0);
    setMode('login');
    resetMessages();
    setShowPassword(false);
    dispatch(hideAuth());
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    setFormData({ email: '', password: '', name: '' });
    setForgotEmail('');
    setOtp('');
    setSignupToken('');
    setUsername('');
    setResendCooldown(0);
    setMode('login');
    resetMessages();
    setShowPassword(false);
  }, [isOpen]);

  const title =
    mode === 'forgot'
      ? 'Reset your password'
      : mode === 'signup-otp'
        ? 'Verify your email'
        : mode === 'signup-username'
          ? 'Choose a username'
          : mode === 'signup'
            ? 'Create your account'
            : 'Welcome back';

  const subtitle =
    mode === 'forgot'
      ? 'Enter your email and we’ll send a reset link.'
      : mode === 'signup-otp'
        ? `Enter the 6-digit code we sent to ${formData.email}.`
        : mode === 'signup-username'
          ? 'This is how other CrafterHack members will see you. You can change it later.'
          : mode === 'signup'
            ? 'Start practicing, competing, and interviewing on CrafterHack.'
            : 'Sign in to continue to CrafterHack.';

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={() => setShowPassword((v) => !v)}
        edge="end"
        size="small"
      >
        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <GoogleOAuthProvider clientId={GOOGLE_SIGN_IN_CLIENT_ID}>
      <Modal
        open={isOpen}
        onClose={handleModalClose}
        aria-labelledby="auth-modal-title"
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: alpha('#020808', 0.72),
              backdropFilter: 'blur(6px)',
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100% - 32px)', sm: 420 },
            maxHeight: 'min(92vh, 720px)',
            overflowY: 'auto',
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            boxShadow: isDark
              ? '0 28px 80px rgba(0,0,0,0.65)'
              : '0 28px 80px rgba(15,60,70,0.18)',
            outline: 'none',
            backgroundImage: isDark
              ? `radial-gradient(ellipse 90% 60% at 50% -20%, ${alpha(ACCENT, 0.28)}, transparent 55%)`
              : `radial-gradient(ellipse 90% 60% at 50% -20%, ${alpha(ACCENT, 0.14)}, transparent 55%)`,
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={handleModalClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: muted,
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ px: { xs: 2.5, sm: 3.25 }, pt: 3.25, pb: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.5 }}>
              <BrandMark />
              <Typography
                id="auth-modal-title"
                sx={{
                  mt: 1.75,
                  fontWeight: 900,
                  fontSize: '1.35rem',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: muted,
                  fontSize: '0.88rem',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  maxWidth: 320,
                }}
              >
                {subtitle}
              </Typography>
            </Box>

            {/* Mode segmented control */}
            {(mode === 'login' || mode === 'signup') && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  p: 0.5,
                  mb: 2.25,
                  borderRadius: 2,
                  bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#0f2a2b', 0.05),
                  border: `1px solid ${border}`,
                }}
              >
                {[
                  { id: 'login', label: 'Sign in' },
                  { id: 'signup', label: 'Sign up' },
                ].map((item) => {
                  const active = mode === item.id;
                  return (
                    <Button
                      key={item.id}
                      onClick={() => switchMode(item.id)}
                      disableElevation
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        py: 0.85,
                        borderRadius: 1.5,
                        color: active ? (isDark ? '#e8f5f5' : '#0b4f51') : muted,
                        bgcolor: active ? (isDark ? alpha(ACCENT, 0.35) : '#fff') : 'transparent',
                        boxShadow: active && !isDark ? '0 1px 3px rgba(15,60,70,0.1)' : 'none',
                        '&:hover': {
                          bgcolor: active
                            ? isDark
                              ? alpha(ACCENT, 0.4)
                              : '#fff'
                            : alpha(ACCENT, 0.08),
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem' }}
                onClose={resetMessages}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem' }}>
                {success}
              </Alert>
            )}

            {/* Forgot password view */}
            {mode === 'forgot' ? (
              <Box component="form" onSubmit={handleForgotSubmit} noValidate>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => switchMode('login')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: muted,
                    mb: 1.5,
                    px: 0,
                    '&:hover': { bgcolor: 'transparent', color: ACCENT },
                  }}
                >
                  Back to sign in
                </Button>
                <TextField
                  label="Email"
                  type="email"
                  name="forgotEmail"
                  autoComplete="email"
                  fullWidth
                  autoFocus
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  sx={{ ...fieldSx(isDark, border), mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon sx={{ fontSize: 18, color: muted }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: ACCENT,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Send reset link'}
                </Button>
              </Box>
            ) : mode === 'signup-otp' ? (
              <Box component="form" onSubmit={handleVerifyOtp} noValidate>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => switchMode('signup')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: muted,
                    mb: 1.5,
                    px: 0,
                    '&:hover': { bgcolor: 'transparent', color: ACCENT },
                  }}
                >
                  Back to sign up
                </Button>
                <OtpInput value={otp} onChange={setOtp} isDark={isDark} border={border} autoFocus />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: ACCENT,
                    boxShadow: 'none',
                    mb: 1.5,
                    '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify code'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: resendCooldown > 0 ? muted : ACCENT,
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    }}
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                  </Button>
                </Box>
              </Box>
            ) : mode === 'signup-username' ? (
              <Box component="form" onSubmit={handleCompleteSignup} noValidate>
                <TextField
                  label="Username"
                  name="username"
                  autoComplete="username"
                  fullWidth
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  helperText="We suggested one based on your email — feel free to change it."
                  FormHelperTextProps={{ sx: { mx: 0.5, color: muted } }}
                  sx={{ ...fieldSx(isDark, border), mb: 2 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: ACCENT,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Create account'}
                </Button>
              </Box>
            ) : (
              <>
                {/* Google */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    '& > div': { width: '100% !important' },
                    '& iframe, & div[role="button"]': { width: '100% !important' },
                  }}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleFailure}
                    theme={isDark ? 'filled_black' : 'outline'}
                    size="large"
                    shape="rectangular"
                    text={mode === 'signup' ? 'signup_with' : 'continue_with'}
                    width="360"
                    locale="en"
                  />
                </Box>

                {/* GitHub */}
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  onClick={handleGithubSignIn}
                  startIcon={<GitHubIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: 1.1,
                    mb: 2,
                    borderRadius: 2,
                    color: isDark ? '#e8f5f5' : '#0b0e11',
                    borderColor: border,
                    '&:hover': {
                      borderColor: isDark ? '#8b949e' : '#0b0e11',
                      bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#0b0e11', 0.04),
                    },
                  }}
                >
                  {mode === 'signup' ? 'Sign up with GitHub' : 'Continue with GitHub'}
                </Button>

                <Divider
                  sx={{
                    my: 2.25,
                    color: muted,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    '&::before, &::after': { borderColor: border },
                  }}
                >
                  or email
                </Divider>

                <Box
                  component="form"
                  onSubmit={mode === 'login' ? handleLogin : handleSignUp}
                  noValidate
                >
                  {mode === 'signup' && (
                    <TextField
                      label="Full name"
                      name="name"
                      autoComplete="name"
                      fullWidth
                      value={formData.name}
                      onChange={handleChange}
                      sx={{ ...fieldSx(isDark, border), mb: 1.5 }}
                    />
                  )}
                  <TextField
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    fullWidth
                    value={formData.email}
                    onChange={handleChange}
                    sx={{ ...fieldSx(isDark, border), mb: 1.5 }}
                  />
                  <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    fullWidth
                    value={formData.password}
                    onChange={handleChange}
                    helperText={
                      mode === 'signup' ? 'At least 8 characters' : undefined
                    }
                    FormHelperTextProps={{ sx: { mx: 0.5, color: muted } }}
                    sx={{ ...fieldSx(isDark, border), mb: mode === 'login' ? 0.5 : 2 }}
                    InputProps={{ endAdornment: passwordAdornment }}
                  />

                  {mode === 'login' && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        onClick={() => {
                          setForgotEmail(formData.email);
                          switchMode('forgot');
                        }}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          color: ACCENT,
                          px: 0.5,
                          minWidth: 0,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        Forgot password?
                      </Button>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      py: 1.25,
                      borderRadius: 2,
                      bgcolor: ACCENT,
                      boxShadow: 'none',
                      fontSize: '0.95rem',
                      '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                      '&.Mui-disabled': {
                        bgcolor: alpha(ACCENT, 0.45),
                        color: '#fff',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : mode === 'login' ? (
                      'Sign in'
                    ) : (
                      'Create account'
                    )}
                  </Button>
                </Box>

                <Typography
                  sx={{
                    mt: 2.25,
                    textAlign: 'center',
                    fontSize: '0.78rem',
                    color: muted,
                    lineHeight: 1.5,
                  }}
                >
                  By continuing, you agree to CrafterHack’s{' '}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setLegalDoc('terms')}
                    sx={{
                      all: 'unset',
                      cursor: 'pointer',
                      color: ACCENT,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                      '&:hover': { color: '#0f5c5e' },
                    }}
                  >
                    Terms
                  </Box>{' '}
                  and acknowledge our{' '}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setLegalDoc('privacy')}
                    sx={{
                      all: 'unset',
                      cursor: 'pointer',
                      color: ACCENT,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                      '&:hover': { color: '#0f5c5e' },
                    }}
                  >
                    Privacy Policy
                  </Box>
                  .
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Modal>

      <LegalModal
        docKey={legalDoc}
        open={!!legalDoc}
        onClose={() => setLegalDoc(null)}
      />
    </GoogleOAuthProvider>
  );
};

export default AuthModal;
